import { useCallback, useMemo } from "react";

import * as Flags from "../Flags";
import * as strings from "../../strings";
import * as StringUtils from "../util/StringUtils";
import * as AuthUtils from "../util/AuthUtils";

import { Group } from "../types/Group";
import { useFlag } from "./MiscHooks";
import { Fleet } from "../types/Fleet";
import {
	GLOBAL_FLEET_ID,
	GLOBAL_FLEET_NAME,
	selectCurrentScopeFleetGroups,
} from "../../store/Fleet/Fleet.selector";
import faker from "../vendor/Faker";
import { useSelector } from "./ReduxHooks";
import {
	selectCurrentScopeFleet,
	selectCurrentScopeGroupId,
	selectLoggedInUser,
} from "../../store/User/User.selector";
import { findGroupById } from "../util/GroupUtils";
import { Invite, User } from "../types/UserTypes";
import { shouldAnonymizeUsers } from "../util/FormatUtils";
import { getDriverKey } from "../util/UserUtils";

export interface FormatHooksOptions {
	noAnonymize?: boolean;
}

const NAME_BLACK_LIST = [
	"Prince",
	"Zelda",
	"Wellington",
	"Elmer",
	"Diego",
	"Willy",
	"Lebsack",
	"Diamond",
];

const findName = () => {
	let fakeFirstName = faker.name.firstName();
	while (NAME_BLACK_LIST.includes(fakeFirstName)) {
		fakeFirstName = faker.name.firstName();
	}
	let fakeLastName = faker.name.lastName();
	while (NAME_BLACK_LIST.includes(fakeLastName)) {
		fakeLastName = faker.name.lastName();
	}
	return [fakeFirstName, fakeLastName];
};

export const useShouldAnonymizeGroups = () => {
	const anonymousMode = useFlag(Flags.ANONYMOUS_MODE);
	const demoMode = useFlag(Flags.DEMO_MODE);

	return anonymousMode.is() && !demoMode.is();
};

export const useFormatUserName = (options?: FormatHooksOptions) => {
	const obfuscateInfo = shouldAnonymizeUsers() && !options?.noAnonymize;
	return useCallback(
		(userOrInvite: User | Invite | undefined | null) => {
			if (!userOrInvite) {
				return undefined;
			}

			const key = getDriverKey(userOrInvite);
			if (key && obfuscateInfo) faker.seed(StringUtils.hashCode(key));
			const { firstName, lastName } = userOrInvite;

			if (!firstName || !lastName) {
				if (obfuscateInfo) {
					const [fakeFirstName, fakeLastName] = findName();
					return firstName ? fakeFirstName : fakeLastName;
				}

				const name = firstName || lastName;
				return StringUtils.toTitleCase(name)?.trim();
			}
			if (obfuscateInfo) {
				const [fakeFirstName, fakeLastName] = findName();
				return `${fakeFirstName} ${fakeLastName}`;
			}

			return `${StringUtils.toTitleCase(firstName)} ${StringUtils.toTitleCase(
				lastName,
			)}`.trim();
		},
		[obfuscateInfo],
	);
};

export const useFormatUserEmail = (options?: FormatHooksOptions) => {
	const obfuscateInfo = shouldAnonymizeUsers() && !options?.noAnonymize;
	return useCallback(
		(userOrInvite: User | Invite | undefined | null) => {
			if (!userOrInvite) {
				return undefined;
			}

			const key = getDriverKey(userOrInvite);
			if (key && obfuscateInfo) faker.seed(StringUtils.hashCode(key));

			if (obfuscateInfo) {
				const [fakeFirstName, fakeLastName] = findName();
				return faker.internet.email(fakeFirstName, fakeLastName).toLowerCase();
			}

			const email = userOrInvite.email;

			// Check if email ends with noemail.brightmile.io and extract username
			if (email && email.endsWith("@noemail.brightmile.io")) {
				let cleanedEmail = email.replace("@noemail.brightmile.io", "");
				if (cleanedEmail.includes("___")) {
					cleanedEmail = cleanedEmail.split("___")[0];
				}
				return cleanedEmail;
			}

			return email;
		},
		[obfuscateInfo],
	);
};

export const useFormatGroupName = (options?: FormatHooksOptions) => {
	const obfuscateInfo = useShouldAnonymizeGroups() && !options?.noAnonymize;
	return useCallback(
		(group: Group | null | undefined) => {
			if (!obfuscateInfo) {
				return group?.name || strings.MISC_GROUP;
			}
			return strings.MISC_UNIT.map((unit) => `${unit} ${(group as any).unit}`);
		},
		[obfuscateInfo],
	);
};

export const useFormatGroupIdName = (options?: FormatHooksOptions) => {
	const { groups } = useSelector((state) => ({
		groups: selectCurrentScopeFleetGroups(state),
	}));

	const obfuscateInfo = useShouldAnonymizeGroups() && !options?.noAnonymize;
	return useCallback(
		(groupId: string | null | undefined) => {
			const group: Group | undefined =
				(groups.value && groupId && findGroupById(groups.value, groupId)) ||
				undefined;

			if (!obfuscateInfo) {
				return group?.name || strings.MISC_GROUP;
			}
			return strings.MISC_UNIT.map((unit) => `${unit} ${(group as any).unit}`);
		},
		[groups.value, obfuscateInfo],
	);
};

export const useFormatFleetName = (options?: FormatHooksOptions) => {
	const obfuscateInfo = useShouldAnonymizeGroups() && !options?.noAnonymize;

	return useCallback(
		(fleet?: Fleet | null) => {
			if (!fleet) {
				return strings.MISC_FLEET;
			}
			const fleetId = fleet.id;

			if (fleetId === GLOBAL_FLEET_ID) return GLOBAL_FLEET_NAME;
			if (fleetId === Flags.DEMO_FLEET_ID || fleetId === Flags.DEMO_FLEET_NAME)
				return Flags.DEMO_FLEET_NAME;

			if (obfuscateInfo) {
				faker.seed(StringUtils.hashCode(fleetId));
				const letter = faker.random.arrayElement([
					"A",
					"B",
					"C",
					"D",
					"E",
					"F",
					"G",
					"H",
					"J",
					"K",
					"L",
					"M",
					"M",
					"N",
					"X",
					"P",
				]);
				return strings.MISC_FLEET.map(
					(fleet) => `${fleet}${fleetId ? " " + letter : ""}`,
				);
			}

			return fleet.name;
		},
		[obfuscateInfo],
	);
};

export const useFormattedScopeFleetName = (options?: FormatHooksOptions) => {
	const { scopeFleet } = useSelector((state) => ({
		scopeFleet: selectCurrentScopeFleet(state),
	}));

	const formatFleetName = useFormatFleetName(options);
	return useMemo(
		() => formatFleetName(scopeFleet.value),
		[formatFleetName, scopeFleet.value],
	);
};

export const useFormattedScopeGroupName = (options?: FormatHooksOptions) => {
	const { scopeGroupId } = useSelector((state) => ({
		scopeGroupId: selectCurrentScopeGroupId(state),
	}));

	const formatGroupIdName = useFormatGroupIdName(options);
	return useMemo(
		() => formatGroupIdName(scopeGroupId),
		[formatGroupIdName, scopeGroupId],
	);
};

export const useFormattedScopeNames = (options?: FormatHooksOptions) => {
	const { scopeGroupId } = useSelector((state) => ({
		scopeGroupId: selectCurrentScopeGroupId(state),
	}));

	const fleetName = useFormattedScopeFleetName(options);
	const groupName = useFormattedScopeGroupName(options);

	return {
		fleetName: fleetName,
		groupName: groupName,
		scopeName: scopeGroupId ? groupName : fleetName,
	};
};

export const useFormattedScopeRootName = (options?: FormatHooksOptions) => {
	const { user } = useSelector((state) => ({
		user: selectLoggedInUser(state),
	}));

	const hasRoot = AuthUtils.canAccessRoot(user);

	const fleetName = useFormattedScopeFleetName(options);
	const groupName = useFormattedScopeGroupName(options);

	return hasRoot ? fleetName : groupName;
};
